import { createContext, useContext, useState, FC, ReactNode, useMemo } from "react";

export enum ResetPasswordStepEnum {
  SendEmail = 0,
  InsertCode = 1,
  ResetPassword = 2,
  Confirmation = 3
}

interface ResetPasswordFlowContextProps {
  currentStep: ResetPasswordStepEnum;
  setCurrentStep: (step: ResetPasswordStepEnum) => void;
  email: string | null;
  setEmail: (email: string) => void;
  workflowId: string | null;
  setWorkflowId: (workflowId: string | null) => void;
  resetFlow: () => void;
  setLanguage: (language: string | null) => void;
  language: string | null;
  mode: "reset" | "change";
}

const ResetPasswordFlowContext = createContext<ResetPasswordFlowContextProps | undefined>(
  undefined
);

export const useResetPasswordFlow = () => {
  const context = useContext(ResetPasswordFlowContext);
  if (!context) {
    throw new Error("useResetPasswordFlow must be used within a ResetPasswordFlowProvider");
  }
  return context;
};

export const ResetPasswordFlowProvider: FC<{ mode: "reset" | "change"; children?: ReactNode }> = ({
  children,
  mode
}) => {
  const [currentStep, setCurrentStep] = useState<ResetPasswordStepEnum>(
    ResetPasswordStepEnum.SendEmail
  );
  const [email, setEmail] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);

  const resetFlow = () => {
    setCurrentStep(ResetPasswordStepEnum.SendEmail);
    setEmail(null);
    setWorkflowId(null);
    setLanguage(null);
  };

  const contextValue = useMemo(
    () => ({
      currentStep,
      setCurrentStep,
      email,
      setEmail,
      workflowId,
      setWorkflowId,
      resetFlow,
      language,
      setLanguage,
      mode
    }),
    [currentStep, email, workflowId, language, mode]
  );

  return (
    <ResetPasswordFlowContext.Provider value={contextValue}>
      {children}
    </ResetPasswordFlowContext.Provider>
  );
};
